/**
 * ttsPolling.js — shared helper that handles the new async TTS pipeline.
 *
 * Backend's `/api/tts/generate` returns one of two shapes:
 *   • { job_id, status: "queued", poll_url }  — async; caller must poll
 *   • { url, ... }                            — legacy sync response
 *
 * `submitAndAwaitTts()` collapses both into a single Promise that
 * resolves to the final audio URL, polling /api/tts/status/{job_id}
 * every 3s for up to 5 minutes when the async path is taken.
 *
 * Usage:
 *   const url = await submitAndAwaitTts({ text, voice, language, contentId });
 */
import { authFetch } from './authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export async function submitAndAwaitTts({
  text,
  voice = 'default',
  language = 'en',
  contentId = '',
  onProgress,                  // optional: ({status, attempt}) callback
  maxWallMs = 5 * 60 * 1000,   // 5 minutes
  pollIntervalMs = 2000,
} = {}) {
  if (!text || !text.trim()) throw new Error('text is required');

  // Submit
  const submitRes = await authFetch(`${BACKEND_URL}/api/tts/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: text.trim(),
      voice,
      language,
      content_id: contentId,
    }),
  });
  const submitData = await submitRes.json().catch(() => ({}));
  if (!submitRes.ok) {
    throw new Error(submitData.detail || submitData.error || `HTTP ${submitRes.status}`);
  }

  // Sync path — already have the URL.
  const syncUrl = submitData.url || submitData.audio_url;
  if (syncUrl) return syncUrl;

  // Async path — poll.
  const jobId = submitData.job_id;
  if (!jobId) throw new Error('TTS response missing both audio URL and job_id');

  const deadline = Date.now() + maxWallMs;
  let attempt = 0;
  while (Date.now() < deadline) {
    attempt += 1;
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    let pollRes;
    try {
      pollRes = await authFetch(`${BACKEND_URL}/api/tts/status/${jobId}`);
    } catch {
      onProgress?.({ status: 'network-blip', attempt });
      continue; // transient — keep polling
    }
    const pollData = await pollRes.json().catch(() => ({}));
    if (!pollRes.ok) {
      throw new Error(pollData.detail || pollData.error || `Poll HTTP ${pollRes.status}`);
    }
    onProgress?.({ status: pollData.status, attempt, ...pollData });

    if (pollData.status === 'done') {
      const finalUrl = pollData.url || pollData.audio_url;
      if (!finalUrl) throw new Error('TTS job completed without an audio URL');
      return finalUrl;
    }
    if (pollData.status === 'error') {
      throw new Error(pollData.error || 'TTS job failed on the server');
    }
    // queued / running / progress → keep polling.
  }
  throw new Error('TTS generation timed out after 5 minutes');
}
